import React from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import debugLib from 'debug';

import classNames from 'classnames';
// TODO, dash_api should move to common
import dash_api from '../../../main/support/dash_api';
import Button from './Button';
import Input from './Input';

import Dropdown from './Dropdown';

import { TIME_TO_DISPLAY_MESSAGE } from '../../support/utils';

import { COUNTRY_CODES } from '../../support/countryCodeConstants';

const debug = debugLib('SendSMS');
const SUCCESS_MESSAGE = '✓ Message sent!';

/**
 * Generic Send SMS Component
 */
class SendSMS extends React.PureComponent {
  static propTypes = {
    /**
     * Extra component classname
     */
    className: PropTypes.string,
    /**
     * Phone number to send link to
     */
    value: PropTypes.string,
    /**
     * Link that will be appended to sms message
     */
    linkToSend: PropTypes.string,
    /**
     * Custom message sent with link
     */
    messageString: PropTypes.string,
    /**
     * Value to show on input box to copy
     */
    placeholder: PropTypes.string,
    /**
     * Text shown on the send button
     */
    sendButtonText: PropTypes.string,
    /**
     * Class name for input component
     */
    inputClassName: PropTypes.string,
    /**
     * Bool to hide dropdown for country code
     */
    useCountryDropDown: PropTypes.bool,
    /**
     * Class name for dropdown outer component
     */
    dropDownOuterClassName: PropTypes.string,
    /**
     * Class name for dropdown main component
     */
    dropDownMainClassName: PropTypes.string,
    /**
     * Class name for real input class name
     */
    inputBoxClassName: PropTypes.string
  };

  static defaultProps = {
    messageString: 'Here is your test link:',
    placeholder: '999-999-9999',
    sendButtonText: 'Send',
    dropDownOuterClassName: 'country-code__select h_va-m',
    dropDownMainClassName: 'date-range'
  };

  static contextTypes = {
    i13n: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);

    this.state = {
      success: false,
      failure: false,
      failureMessage: 'Please try a different number',
      value: '',
      customValue: '',
      loading: false,
      countryCodeDropDownValue: '+1'
    };
  }

  componentWillMount() {
    this.setState({
      value: this.props.value
    });
  }

  componentWillReceiveProps(nextProps) {
    if (R.equals(nextProps, this.props)) {
      return;
    }

    this.setState({
      value: nextProps.value
    });
  }

  fireMixpanelError = failureMessage => {
    if (!this.context.i13n) {
      return;
    }
    this.context.i13n.executeEvent('log', {
      label: `Send SMS error. ${failureMessage}.`,
      params: {
        component: 'SendSMS'
      }
    });
  };

  /**
   * Send SMS
   */
  sendSMS = () => {
    if (!this.state.value) {
      this.setState({ failure: true, failureMessage: 'Please enter a number' });
      setTimeout(() => {
        this.setState({ failure: false });
      }, TIME_TO_DISPLAY_MESSAGE);
      this.fireMixpanelError('Please enter a number');
      return;
    }

    debug(
      'sending message:\n',
      this.props.messageString,
      '\n',
      this.props.linkToSend,
      '\nto: ',
      this.state.value
    );

    this.setState({ loading: true });

    dash_api
      .post('/v1/sms', {
        to_number: this.state.countryCodeDropDownValue + this.state.value,
        message: `${this.props.messageString} ${this.props.linkToSend}`
      })
      .then(x => {
        debug('Message sent successfully: ', x.data);
        this.setState({ success: true, loading: false });
        setTimeout(() => {
          this.setState({ success: false });
        }, TIME_TO_DISPLAY_MESSAGE);
      })
      .catch(err => {
        debug('Error sending sms: ', err.data);
        let failureMessage = this.state.failureMessage;
        if (err.data && err.data.message) {
          try {
            var messageObj = JSON.parse(err.data.message);
            if (messageObj.message) {
              failureMessage = messageObj.message;
            }
          } catch (error) {
            debug('unable to parse error message', error);
          }
        }
        this.setState({ failure: true, failureMessage, loading: false });
        setTimeout(() => {
          this.setState({ failure: false });
        }, TIME_TO_DISPLAY_MESSAGE);
        this.fireMixpanelError(failureMessage);
      });
  };

  /**
   * Update input value
   * @param {Object} e payload
   */
  updateInputValue = e => {
    const { value, error } = e;
    if (!error) {
      this.setState({
        value
      });
    }
  };

  _onChange = valueLabelObj => {
    // The parent only cares about the value, not the label
    this.setState({
      countryCodeDropDownValue: valueLabelObj.value
    });
  };

  _renderKeyOptions = (option: { label: string }) => (
    <div className="h_d-f h_jc-sb">{option.label}</div>
  );

  render() {
    const {
      className,
      inputClassName,
      buttonClassName,
      buttonClassType,
      dropDownOuterClassName,
      dropDownClassName,
      dropDownMainClassName,
      inputBoxClassName,
      placeholder,
      sendButtonText
    } = this.props;

    return (
      <div
        className={classNames('sms h_w-100', className, {
          'sms--success': this.state.success,
          'sms--failure': this.state.failure
        })}
        data-testid="send-sms"
      >
        <div className={classNames('dropdown', dropDownMainClassName)}>
          {this.props.useCountryDropDown && (
            <Dropdown
              options={COUNTRY_CODES}
              searchable={true}
              value={this.state.countryCodeDropDownValue}
              clearable={false}
              onChange={this._onChange}
              outerClassName={dropDownOuterClassName}
              i13nLabel="Country Code List"
              optionRenderer={this._renderKeyOptions}
              testid="sms-country-dropdown"
              className={dropDownClassName}
            />
          )}
          <Input
            className={classNames('sms__input h_va-m', inputClassName)}
            inputClassName={inputBoxClassName}
            onChange={this.updateInputValue}
            footnote={
              this.state.success
                ? SUCCESS_MESSAGE
                : this.state.failure
                ? this.state.failureMessage
                : null
            }
            value={this.state.value}
            placeholder={placeholder}
            ref="sms-input"
            testid="sms-input"
            type="tel"
            i13nLabel="SMS - Phone Number input"
          />
          <Button
            onClick={this.sendSMS}
            className={classNames('sms__btn h_va-t', buttonClassName)}
            type={buttonClassType}
            aria-label="Send SMS"
            title="Send SMS"
            i13nModel={{ label: 'Send SMS' }}
            testid="send-sms"
          >
            {this.state.loading ? (
              <img width="20px" src="/static/images/loading-spinner.gif" />
            ) : (
              sendButtonText
            )}
          </Button>
        </div>
      </div>
    );
  }
}

export default SendSMS;
